// src/utils/groupSchedule.js
// Shared helpers for tutor group schedules. Used by the tutor dashboard
// (creating / editing groups) and the admin "Today's sessions" box.
//
// A group's schedule lives in `sessions`: [{ day: 'Wednesday', time: '18:00' }]
// Times are India time, 24-hour "HH:MM". Every session lasts 30 minutes.
// The group's `name` is always built from `sessions` — never typed by hand.

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const SESSION_LENGTH_MIN = 30;

// Converts hour/minute/am-pm pieces to minutes after midnight, or null.
function toMinutes(hourStr, minStr, meridian) {
  let h = parseInt(hourStr, 10);
  const min = minStr ? parseInt(minStr, 10) : 0;
  const ampm = meridian ? meridian.replace(/\./g, '').toLowerCase() : null;
  if (Number.isNaN(h) || min > 59) return null;
  if (ampm) {
    if (h < 1 || h > 12) return null;
    if (ampm === 'pm' && h < 12) h += 12;
    if (ampm === 'am' && h === 12) h = 0;
  }
  if (h > 23) return null;
  return h * 60 + min;
}

// The `time` field: "21:00", "9:00", "9:00 PM", "9 pm" → minutes, or null.
export function parseTime(str) {
  if (!str) return null;
  const m = String(str).trim().match(/^(\d{1,2})(?:\s*[:.]\s*(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?$/i);
  if (!m) return null;
  return toMinutes(m[1], m[2], m[3]);
}

// Minutes after midnight → "18:00" (the format saved in Firestore).
export function toHHMM(minutes) {
  if (minutes === null || minutes === undefined) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// "18:00" → "6:00 PM" (the format shown in titles).
export function formatTime12(time) {
  const minutes = parseTime(time);
  if (minutes === null) return '';
  const h24 = Math.floor(minutes / 60);
  const m = minutes % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${h24 < 12 ? 'AM' : 'PM'}`;
}

// Sessions with both a day and a valid time, in weekday order.
export function cleanSessions(sessions) {
  return (sessions || [])
    .filter(s => s && s.day && parseTime(s.time) !== null)
    .map(s => ({ day: s.day, time: toHHMM(parseTime(s.time)) }))
    .sort((a, b) => DAYS.indexOf(a.day) - DAYS.indexOf(b.day));
}

// Builds the group title from its sessions:
//   same time every day → "Wednesday & Saturday 6:00 PM"
//   different times     → "Sunday 6:00 PM & Tuesday 3:25 PM"
export function buildGroupName(sessions) {
  const list = cleanSessions(sessions);
  if (list.length === 0) return '';
  const sameTime = list.every(s => s.time === list[0].time);
  if (sameTime) {
    return `${list.map(s => s.day).join(' & ')} ${formatTime12(list[0].time)}`;
  }
  return list.map(s => `${s.day} ${formatTime12(s.time)}`).join(' & ');
}

// Fallback for older groups whose `time` is empty: read the start time from
// the title. Handles "Monday 19:22", "wed 3 pm", "6 to 6:30 PM",
// "7:30 to 8: 00 PM". Returns null if there is no time, or several different
// start times, because then it can't tell which time belongs to which day.
const MERIDIAN = '(am|pm|a\\.m\\.|p\\.m\\.)';
const RANGE_RE = new RegExp(
  `\\b(\\d{1,2})(?:\\s*[:.]\\s*(\\d{2}))?\\s*${MERIDIAN}?\\s*(?:to|-|–)\\s*(\\d{1,2})(?:\\s*[:.]\\s*(\\d{2}))?\\s*${MERIDIAN}?`,
  'gi'
);
const SINGLE_RE = new RegExp(
  `\\b(\\d{1,2})\\s*[:.]\\s*(\\d{2})\\s*${MERIDIAN}?|\\b(\\d{1,2})\\s*${MERIDIAN}`,
  'gi'
);

export function parseStartFromTitle(title) {
  if (!title) return null;
  const starts = new Set();
  let rest = String(title);

  // Ranges like "6 to 6:30 PM": the start takes the end's AM/PM if it has none.
  rest = rest.replace(RANGE_RE, (match, h1, m1, mer1, h2, m2, mer2) => {
    const end = toMinutes(h2, m2, mer2);
    let start = toMinutes(h1, m1, mer1 || mer2);
    // "11:30 to 12 PM" — inheriting PM would put the start after the end.
    if (!mer1 && mer2 && start !== null && end !== null && start > end) {
      start = toMinutes(h1, m1, mer2.toLowerCase().startsWith('p') ? 'am' : 'pm');
    }
    if (start !== null) starts.add(start);
    return ' ';
  });

  // Single times like "19:22" or "3 pm".
  rest.replace(SINGLE_RE, (match, h1, m1, mer1, h2, mer2) => {
    const t = h1 !== undefined ? toMinutes(h1, m1, mer1) : toMinutes(h2, null, mer2);
    if (t !== null) starts.add(t);
    return match;
  });

  return starts.size === 1 ? [...starts][0] : null;
}

// Start time for one session: the `time` field wins; the title is a fallback.
export function getSessionStartMinutes(session, groupName) {
  const fromField = parseTime(session?.time);
  if (fromField !== null) return fromField;
  return parseStartFromTitle(groupName);
}